/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import type { SoundingFeature } from '../../api/edr-api/types';
import type { SoundingPanel } from '../../store/types';
import type { SoundingsProfileDataPreferences } from '../../utils/profileDataPreferences';
import { HodographCollection } from '../Hodograph/HodographCollection';
import type { ResizablePanelComponent } from '../ResizablePanel/ResizablePanelStandalone';
import { SortablePanels } from '../SortablePanels/SortablePanels';
import type { SortablePanelItem } from '../SortablePanels/SortablePanels';
import { DiagramTypeButton } from '../Soundings/DiagramTypeButton';
import { Soundings } from '../Soundings/Soundings';
import { useSoundingsDiagramSize } from '../Soundings/useSoundingsDiagramSize';
import { WindBarbs } from '../Soundings/WindBarbs';
import { SoundingsProfileDataTable } from '../SoundingsProfileDataTable/SoundingsProfileDataTable';

export interface SoundingsPanelsProps {
  viewId: string;
  soundingPanelsInOrder: SoundingPanel[];
  data: SoundingFeature[][];
  chosenDiagramType: 'SKEWT' | 'BIJVOET' | 'EMAGRAM';
  setChosenDiagramType: (type: 'SKEWT' | 'BIJVOET' | 'EMAGRAM') => void;
  onOrderChange: (
    newOrder: { id: string; name: string; isDisplayed: boolean }[],
  ) => void;
  panelComponent?: ResizablePanelComponent;
  profileDataPreferences?: SoundingsProfileDataPreferences;
}

export const SoundingsPanels = ({
  viewId,
  soundingPanelsInOrder,
  data,
  chosenDiagramType,
  setChosenDiagramType,
  onOrderChange,
  panelComponent,
  profileDataPreferences,
}: SoundingsPanelsProps): React.ReactElement => {
  const soundingsContentRef = React.useRef<HTMLDivElement>(null);
  const windBarbsContentRef = React.useRef<HTMLDivElement>(null);
  const diagramSize = useSoundingsDiagramSize(soundingsContentRef);
  const windBarbsDiagramSize = useSoundingsDiagramSize(
    windBarbsContentRef,
    false,
  );
  const soundingsIsOpen = React.useMemo(
    () =>
      soundingPanelsInOrder.find((panel) => panel.id === 'soundings')?.isOpen ||
      false,
    [soundingPanelsInOrder],
  );

  const [previousHeight, setPreviousHeight] = React.useState<number | null>(
    null,
  );
  const firstSelectedSourceData = React.useMemo(() => data[0] ?? [], [data]);

  const windBarbsHeight = React.useMemo(() => {
    if (soundingsIsOpen) {
      return diagramSize;
    }
    return previousHeight ?? windBarbsDiagramSize;
  }, [diagramSize, previousHeight, soundingsIsOpen, windBarbsDiagramSize]);

  const sizeRef = React.useRef(diagramSize);
  const isResizingRef = React.useRef(false);
  React.useEffect(() => {
    sizeRef.current = diagramSize;
  }, [diagramSize]);

  React.useEffect(() => {
    if (previousHeight == null && diagramSize > 24) {
      setPreviousHeight(diagramSize);
    }
  }, [diagramSize, previousHeight]);

  const preserveWindBarbsGrowBackHeight = React.useCallback(() => {
    const nextHeight =
      sizeRef.current > 24 ? sizeRef.current : windBarbsDiagramSize;
    setPreviousHeight(nextHeight);
  }, [windBarbsDiagramSize]);

  const handleSoundingsResizeStart = React.useCallback(() => {
    isResizingRef.current = true;
    preserveWindBarbsGrowBackHeight();
  }, [preserveWindBarbsGrowBackHeight]);

  const handleSoundingsResizeEnd = React.useCallback(() => {
    isResizingRef.current = false;
  }, []);

  const handleSoundingsOpenChange = React.useCallback(
    (isOpen: boolean) => {
      const isClosingViaClick = !isOpen && !isResizingRef.current;
      if (isClosingViaClick) {
        preserveWindBarbsGrowBackHeight();
      }
    },
    [preserveWindBarbsGrowBackHeight],
  );

  const panels = soundingPanelsInOrder.flatMap(
    ({ id, isDisplayed }): SortablePanelItem[] => {
      const normalizedId = id.toLowerCase().replace(/\s+/g, '');
      switch (normalizedId) {
        case 'soundings':
          return [
            {
              id: normalizedId,
              name: 'Soundings',
              isDisplayed,
              headerAction: (
                <DiagramTypeButton
                  diagramType={chosenDiagramType}
                  setDiagramType={setChosenDiagramType}
                />
              ),
              panelRef: soundingsContentRef,
              onOpenChange: handleSoundingsOpenChange,
              onResizeStart: handleSoundingsResizeStart,
              onResizeEnd: handleSoundingsResizeEnd,
              render: () => (
                <Soundings
                  d3GraphId={normalizedId}
                  data={data}
                  diagramType={chosenDiagramType}
                  svgDimensions={{
                    width: diagramSize,
                    height: diagramSize,
                    margin: { bottom: 25, left: 25, right: 25, top: 0 },
                  }}
                />
              ),
            },
          ];
        case 'windbarbs':
          return [
            {
              id: normalizedId,
              name: 'Wind Barbs',
              isDisplayed,
              maxWidth: 200,
              panelRef: windBarbsContentRef,
              render: () => (
                <WindBarbs
                  animateResize={Boolean(!soundingsIsOpen && previousHeight)}
                  d3GraphId={normalizedId}
                  data={data}
                  diagramType={chosenDiagramType}
                  svgDimensions={{
                    width: 100,
                    margin: { bottom: 25, left: 25, right: 25, top: 0 },
                    height: windBarbsHeight,
                  }}
                />
              ),
            },
          ];
        case 'hodograph':
          return [
            {
              id: normalizedId,
              name: 'Hodograph',
              isDisplayed,
              maxWidth: 500,
              render: () => <HodographCollection data={data} />,
            },
          ];
        case 'profiledatatable':
          return [
            {
              id: normalizedId,
              name: 'Profile Data',
              isDisplayed,
              maxWidth: 500,
              render: () => (
                <SoundingsProfileDataTable
                  features={firstSelectedSourceData}
                  maxHeight="100%"
                  preferences={profileDataPreferences}
                />
              ),
            },
          ];
        default:
          return [];
      }
    },
  );

  return (
    <SortablePanels
      onOrderChange={onOrderChange}
      panelComponent={panelComponent}
      panels={panels}
      viewId={viewId}
    />
  );
};
